import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
  PracticeBotService,
  PracticeEndedReason,
  PracticeSessionCreateResponse,
  PracticeTranscriptTurn,
} from './practice-bot.service';

/** How long the caption may take to catch up once the bot has stopped speaking. */
const CATCH_UP_MS = 1200;

/** Cap on waiting for the bot's closing audio, so a stuck stream can't hold the call open. */
const BOT_FINISH_MAX_WAIT_MS = 8000;

/** Head-room past the estimated end of the closing line before the cap takes over. */
const BOT_FINISH_SLACK_MS = 1200;

/**
 * Silence after the coach finishes before we ask it to check in. semantic_vad only ever fires off
 * SPEECH, so a learner who says nothing produces no speech_started, no committed turn and no
 * response — and both sides wait for each other until the duration cap. This timer is the only
 * thing that breaks that deadlock.
 */
const IDLE_NUDGE_MS = 60000;
/** After this many unanswered check-ins the call ends — as a timeout, not as a user hang-up. */
const MAX_IDLE_NUDGES = 3;
/**
 * Directions for the coach, never canned lines: it has to improvise in character and in the language
 * of the session. These are injected as a system MESSAGE (see nudgeIdleLearner) rather than passed as
 * `response.instructions`, which would replace the session instructions for that one turn.
 */
const IDLE_INSTRUCTIONS = [
  'The learner has gone quiet. Warmly check whether they are still there. One sentence, in character, in the language you have been speaking.',
  'The learner is still silent. Briefly offer to repeat or rephrase your last point. One sentence, in character, in the language you have been speaking.',
  'The learner has not responded at all. Say you will wrap up for now if they cannot continue. One sentence, in character, in the language you have been speaking.',
];

/** How long ICE may sit in 'disconnected' before the call is treated as lost. It often recovers. */
const ICE_RECOVERY_GRACE_MS = 5000;

/**
 * How long to wait for a recorder's `onstop` before giving up on it. Both recorders are stopped in
 * turn, so this is paid twice in the worst case, before the end/analysis path can go on. Generous
 * rather than tight: `stop()` normally settles in a frame or two, and settling early would resolve
 * without the final `dataavailable` chunk — silently truncating the end of the upload.
 */
const RECORDER_STOP_MAX_WAIT_MS = 1500;

/** Time-domain RMS above which the remote track counts as "still speaking". */
const SPEAKING_RMS_THRESHOLD = 0.01;

/** Silence this long at the start of a turn means the analyser can't hear the bot: reveal on the clock. */
const REVEAL_AUDIO_WAIT_MS = 1500;

/** Quiet this long and the bot has stopped talking, rather than being between two words. */
const AUDIO_STOPPED_MS = 400;

/** ~66ms/char ≈ 150 wpm: how long the bot's voice takes to say a line of a given length. */
const SPOKEN_MS_PER_CHAR = 66;

/** Gap between the first transcript delta and the first audible sample of that turn. */
const AUDIO_LEAD_IN_MS = 400;

/** Server-VAD tuning. The API default (0.5) trips on room noise and speaker bleed. */
const VAD_THRESHOLD = 0.6;
const VAD_PREFIX_PADDING_MS = 300;
/**
 * Silence that ends the learner's turn.
 *
 * 700ms is shorter than an ordinary thinking pause, so the coach took the floor mid-sentence — and
 * everything after that is a consequence: the learner talks over the reply, the reply is cancelled
 * part-spoken (the audible glitch), the remainder of the sentence becomes a SECOND turn, and the
 * model answers with the earlier half still in its context, which is why it can repeat itself.
 *
 * Raising it costs a little latency on every turn — the coach waits this long after the learner
 * really has stopped — and buys back the interruption cascade.
 */
const VAD_SILENCE_MS = 1500;

/** Cap on waiting for the learner's transcript when none of it streamed in. Dead air — keep tight. */
const USER_TRANSCRIPT_WAIT_MS = 1200;

/** Punctuation the transcriber puts around noise — stripped before the length test. */
const PUNCTUATION = /[\s.,!?¿¡'"“”‘’()\[\]{}:;\-–—…]/g;

/** Trimmed of punctuation, a learner turn shorter than this is room noise, not speech. */
const MIN_USER_SPEECH_CHARS = 2;
/**
 * A cough, a door or a burst of speaker bleed is brief. Past this, the VAD heard a real utterance,
 * so an empty transcript means the ASR lost it — never that nothing was said.
 */
const MAX_NOISE_MS = 700;
/** Noise-only turns in a row before the screen says so. */
const NOISE_WARNING_AFTER = 3;

/** Stamped on our session.update so the error event that rejects it is identifiable. */
const SESSION_TUNING_EVENT_ID = 'practice-session-tuning';

/** What the call hands back for step 3 (upload + end). */
export interface PracticeCallResult {
  transcript: PracticeTranscriptTurn[];
  /** Learner mic only — drives the analysis. */
  micAudio?: Blob;
  /** Mixed learner + bot — stored for replay. */
  mixedAudio?: Blob;
  /**
   * How long the learner's recording actually runs, in seconds.
   *
   * Timed from the recorder rather than the call: the session is created, the mic is acquired and
   * SDP is exchanged before any of this starts, so the call's own clock always reads longer than
   * the track. Measured rather than read back off the blob because a WebM from MediaRecorder
   * reports `Infinity` for its duration until something rewrites its metadata.
   */
  micAudioSeconds?: number;
  endedReason: PracticeEndedReason;
}

/**
 * Step 2 of the practice flow: the live voice call, WebRTC straight to OpenAI.
 *
 * Persona, behaviour and the end_conversation tool are baked into the client_secret server-side; we
 * only PATCH that session to tighten turn detection and own response creation (applySessionTuning).
 *
 * Responsibilities:
 *  · mic capture + RTCPeerConnection + SDP handshake (via PracticeBotService.connectRealtimeCall)
 *  · make the bot speak first (response.create, with a 1.2s fallback)
 *  · gate each bot reply behind the learner's own transcript, and swallow noise-only turns
 *  · collect the transcript with client-clock timestamps (bot turn boundaries exist ONLY here)
 *  · record mic-only (analysis) and mixed mic+bot (playback)
 *  · surface bot_ended / timeout / disconnected so the modal can run step 3
 */
@Injectable({ providedIn: 'root' })
export class PracticeVoiceCallService {
  /** Remote (bot) audio — attach to an <audio autoplay> so the learner can hear it. */
  readonly remoteStream$ = new Subject<MediaStream>();
  /** Emits the running transcript (COMPLETED turns) so the voice screen can show the history. */
  readonly transcriptChange$ = new Subject<PracticeTranscriptTurn[]>();
  /**
   * The turn currently being spoken, updated on every delta, or null between turns. Lets the screen
   * show "A finished · B is talking now" instead of nothing until a turn completes.
   */
  readonly liveTurn$ = new Subject<{ role: 'user' | 'assistant'; text: string } | null>();
  /** The call wants to end by itself: bot_ended · timeout · disconnected. */
  readonly ended$ = new Subject<PracticeEndedReason>();
  /**
   * Fires once a run of turns has been committed by the VAD and transcribed to nothing — the room is
   * noisy, or the mic is picking up something that is not the learner. Dropping those turns silently
   * (see releaseUserTurn) leaves a learner watching a coach that answers nothing; this makes the
   * cause visible without blocking the call.
   */
  readonly noiseWarning$ = new Subject<void>();
  readonly error$ = new Subject<any>();

  /**
   * Caption pace, ms per character. A ceiling, not a clock: it only advances while the bot is audible
   * (pumpReveal). 0 reveals instantly (tests).
   *
   * The voice's own rate, deliberately. At 40 the caption ran 1.65x the speaker, so it started with
   * the voice and finished a long reply seconds early — the reader waits in silence for audio that
   * is still mid-sentence, which is the same complaint as a caption that starts too soon.
   */
  revealMsPerChar = SPOKEN_MS_PER_CHAR;

  /** Quiet needed before we accept the bot has finished its closing line. 0 ends at once (tests). */
  botFinishGraceMs = 700;

  // ── Turn-detection tuning (set before start(); patched onto the server's session) ────────────
  /** Raise towards 1 if the bot still answers room noise; lower if quiet speech is missed. */
  vadThreshold = VAD_THRESHOLD;
  /** Silence that ends the learner's turn. Shorter feels snappier but clips thinking pauses. */
  vadSilenceMs = VAD_SILENCE_MS;
  /** 'near_field' for headsets/laptop mics, 'far_field' for speakerphone, '' to leave it off. */
  noiseReduction: 'near_field' | 'far_field' | '' = 'near_field';
  /** Hold each bot reply until the learner's line is on screen. Off = the server answers immediately. */
  waitForUserTranscript = true;

  /**
   * Record the mixed learner+bot replay clip. Set false where WebAudio and a live WebRTC call don't
   * coexist (WKWebView): the call then records mic-only, which is the track the analysis needs.
   *
   * Off in Firefox. The mix taps BOTH live streams — createMediaStreamSource on the microphone that
   * is being sent, and on the remote track that is being played — and Firefox does not reliably feed
   * one MediaStream to an <audio> element and a WebAudio node at the same time. The call goes silent
   * in both directions, which is worse than losing a replay clip. Cost: no replay recording on
   * Firefox; the mic track that drives the analysis is unaffected.
   */
  recordPlaybackMix = !/firefox/i.test(
    (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  );

  private pc: RTCPeerConnection;
  private dataChannel: RTCDataChannel;
  private micStream: MediaStream;
  private remoteStream: MediaStream;

  private micRecorder: MediaRecorder;
  /** When the learner's recorder actually began, for the track length reported at /end. */
  private micRecordingStartedAt = 0;

  /** Monotonic clock, overridable in tests. Never Date.now() — see startRecording. */
  protected now(): number {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  }
  private mixedRecorder: MediaRecorder;
  private micChunks: Blob[] = [];
  private mixedChunks: Blob[] = [];
  private audioContext: AudioContext;
  private mixedDestination: MediaStreamAudioDestinationNode;
  /** The bot is already routed into the mix — connecting again would double its level. */
  private remoteMixConnected = false;
  /** Pending 'disconnected' grace period — cleared the moment ICE recovers. */
  private disconnectTimer: any;
  /** ended$ is one-shot per call: the handler fires on every later state change. */
  private endedEmitted = false;

  private transcript: PracticeTranscriptTurn[] = [];
  private greetingSent = false;
  private greetingFallbackTimer: any;
  private maxDurationTimer: any;
  private startedAtMs = 0;
  private active = false;

  // Partial turns being assembled from the data-channel event stream.
  private userTurnStartedAt = 0;
  private userTurnEndedAt = 0;
  private assistantTurnStartedAt = 0;
  private assistantTextBuffer = '';
  private userTextBuffer = '';

  /** The session the server minted (session.created) — the base we patch, never replace. */
  private serverSession: any;
  /** The server has CONFIRMED it stopped auto-answering, so we own response.create. */
  private manualResponseMode = false;
  /** A response is streaming; a second response.create would be rejected. */
  private responseInFlight = false;
  private idleTimer: any;
  private idleNudges = 0;
  /** Consecutive VAD turns that transcribed to nothing. */
  private noiseDrops = 0;
  /** A reply is owed but one was still streaming — fire it on response.done. */
  private pendingResponseRequest = false;
  private userTranscriptTimer: any;
  /** The learner talked over the bot — dropping this turn silently would kill the call. */
  private interruptedActiveResponse = false;
  /** The line already published for this utterance, so a late transcript can supersede it. */
  private userTurnEntry: PracticeTranscriptTurn;
  /** A reply has been asked for on this utterance; a late transcript must not ask again. */
  private userTurnReleased = false;
  /** Item id of the audio the VAD committed, so noise can be removed from context. */
  private lastCommittedItemId = '';
  /** Event types we don't handle, logged once each so a renamed event is easy to spot. */
  private seenUnhandledTypes = new Set<string>();

  constructor(private practiceBotService: PracticeBotService) {}

  isActive(): boolean {
    return this.active;
  }

  /** Milliseconds since the call connected — for the on-screen timer. */
  get elapsedMs(): number {
    return this.startedAtMs ? Date.now() - this.startedAtMs : 0;
  }

  getTranscript(): PracticeTranscriptTurn[] {
    return this.transcript;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  /** Opens mic, connects to OpenAI, and starts both recorders. */
  /**
   * Start acquiring the microphone before the session is minted. getUserMedia needs nothing from the
   * session, but is the slowest step of the handshake (and on the first run it waits for the learner
   * to grant permission), so leaving it behind the session POST adds its full cost to the wait before
   * the coach's first word. Safe to call more than once; releasePrewarmedMicrophone() if the call
   * never happens.
   */
  prewarmMicrophone(): void {
    if (this.micPrewarm || this.micStream) { return; }
    const pending = this.getMicrophoneStream();
    this.micPrewarm = pending;
    // start() retries and surfaces the real error; this only keeps the rejection from going unhandled.
    pending.catch(() => undefined);
  }

  /**
   * Settle the microphone and hand back the stream, WITHOUT consuming it — start() still collects the
   * same one, so the learner is asked at most once. Callers use this to find out whether a call is
   * possible at all before doing anything that costs something.
   */
  microphoneReady(): Promise<MediaStream> {
    if (this.micStream) { return Promise.resolve(this.micStream); }
    this.prewarmMicrophone();
    return this.micPrewarm ?? this.getMicrophoneStream();
  }

  /** Drop a pre-warmed microphone that will never be used (session failed, modal closed). */
  releasePrewarmedMicrophone(): void {
    const pending = this.micPrewarm;
    this.micPrewarm = undefined;
    if (!pending) { return; }
    pending.then(
      (stream) => stream?.getTracks?.().forEach((track) => track.stop()),
      () => undefined
    );
  }

  /** The pre-warmed stream if there is one, otherwise acquired now. */
  private acquireMicrophone(): Promise<MediaStream> {
    const pending = this.micPrewarm;
    this.micPrewarm = undefined;
    // A pre-warm that failed (or was denied and since granted) must not sink the call.
    return pending ? pending.catch(() => this.getMicrophoneStream()) : this.getMicrophoneStream();
  }

  async start(session: PracticeSessionCreateResponse): Promise<void> {
    // A previous call still holding the microphone, the peer connection or the audio graph has to be
    // released, not merely dereferenced: reset() drops those references without closing anything, so
    // starting over a live call would leave the mic recording and the peer open for the rest of the
    // modal session. Not stop() — that would report the old call as `ended` to a screen which has
    // already moved on. Checked before reset(), while the references are still there to find.
    if (this.active || this.pc || this.micStream) { this.abandonCall(); }
    this.reset();

    this.micStream = await this.acquireMicrophone();
    try {
      await this.connect(session);
    } catch (error) {
      // Everything from here on can reject — a refused OpenAI handshake is a realistic path — and
      // `active` never became true, so the modal's ngOnDestroy skips stop() (it guards on
      // isActive()) and openVoiceCall only shows a message. Without releasing here the learner is
      // left with a live microphone and an open peer connection for the rest of the modal session.
      this.abandonCall();
      throw error;
    }
  }

  /** The connect sequence proper — wrapped by start() so a failure cannot leak the mic or the peer. */
  private async connect(session: PracticeSessionCreateResponse): Promise<void> {
    this.pc = this.createPeerConnection();
    this.remoteStream = this.createMediaStream();

    this.pc.ontrack = (event: RTCTrackEvent) => this.handleRemoteTrack(event);
    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      if (state === 'failed') {
        this.endCall('disconnected');   // terminal: ICE will not recover from this
        return;
      }
      if (state === 'disconnected') {
        // Recoverable. ICE drops to 'disconnected' on a brief network blip and returns to
        // 'connected' on its own, so ending here would tear down a call that is still usable.
        // Give it a grace period and only end if it is still down when that expires.
        if (!this.disconnectTimer) {
          this.disconnectTimer = setTimeout(() => {
            this.disconnectTimer = undefined;
            if (this.pc?.connectionState === 'disconnected') { this.endCall('disconnected'); }
          }, ICE_RECOVERY_GRACE_MS);
        }
        return;
      }
      // Anything else — connected, connecting, new — means the blip resolved.
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = undefined;
    };

    this.micStream.getTracks().forEach((track) => this.pc.addTrack(track, this.micStream));

    // Named 'oai-events' by convention; this is where the transcript and end_conversation arrive.
    this.dataChannel = this.pc.createDataChannel('oai-events');
    this.dataChannel.onmessage = (event: MessageEvent) => this.handleServerEvent(event);
    this.dataChannel.onopen = () => {
      // Fallback for when session.created/updated never lands — otherwise both sides stay silent.
      this.greetingFallbackTimer = setTimeout(() => this.triggerBotGreeting(), 1200);
    };

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const answerSdp = await this.practiceBotService.connectRealtimeCall(
      session?.client_secret,
      offer.sdp,
      session?.realtime_model
    );
    await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp } as RTCSessionDescriptionInit);

    this.startRecording();
    this.active = true;
    this.startedAtMs = Date.now();

    const maxSeconds = session?.max_duration_seconds || 600;
    this.maxDurationTimer = setTimeout(() => this.endCall('timeout'), maxSeconds * 1000);
  }

  /**
   * Emits the one "this call is over" signal. Guarded because onconnectionstatechange keeps firing
   * as the peer winds down, and the max-duration timer can land on top of a bot_ended — either
   * would send the modal through its end/analysis path a second time.
   */
  private endCall(reason: PracticeEndedReason) {
    if (this.endedEmitted) { return; }
    this.endedEmitted = true;
    this.ended$.next(reason);
  }

  /**
   * Releases a call that never came up: mic tracks, peer connection, data channel, audio graph and
   * timers. Deliberately not stop(): there is no session to end, no recording worth returning, and
   * ended$ must stay quiet so the modal does not run its end/analysis path for a call that never
   * started.
   */
  private abandonCall() {
    this.clearTimers();
    this.active = false;
    try { this.dataChannel?.close(); } catch (e) { /* never opened */ }
    this.micStream?.getTracks()?.forEach((track) => track.stop());
    try { this.pc?.close(); } catch (e) { /* never opened */ }
    this.teardownMix();
    this.dataChannel = undefined;
    this.pc = undefined;
    this.micStream = undefined;
  }

  /** Tears everything down and returns the recordings + transcript for step 3. */
  async stop(endedReason: PracticeEndedReason = 'user_ended'): Promise<PracticeCallResult> {
    // Anything mid-reveal was still said, so commit it or the upload loses the bot's last turn.
    if (this.assistantTextBuffer) { this.commitAssistantTurn(); }
    this.clearTimers();
    this.active = false;

    // Read before stopping: stopRecorder awaits the recorder's last chunk, and the wait is not
    // part of the recording.
    const measured = this.micRecordingStartedAt
      ? Math.round((this.now() - this.micRecordingStartedAt) / 100) / 10
      : 0;
    // Nothing rather than zero. A recorder that ran has a length, so a zero here means it never
    // really started — and /end keeps the FIRST value it is given, so a wrong zero is permanent.
    const micAudioSeconds = measured > 0 ? measured : undefined;
    this.micRecordingStartedAt = 0;
    const micAudio = await this.stopRecorder(this.micRecorder, this.micChunks);
    const mixedAudio = await this.stopRecorder(this.mixedRecorder, this.mixedChunks);

    try { this.dataChannel?.close(); } catch (e) { /* already gone */ }
    this.micStream?.getTracks()?.forEach((track) => track.stop());
    try { this.pc?.close(); } catch (e) { /* already gone */ }
    try { this.audioContext?.close(); } catch (e) { /* already gone */ }

    const result: PracticeCallResult = {
      transcript: this.transcript,
      micAudio,
      mixedAudio,
      micAudioSeconds,
      endedReason,
    };
    this.pc = undefined;
    this.dataChannel = undefined;
    this.micStream = undefined;
    this.audioContext = undefined;
    this.mixedDestination = undefined;
    this.remoteAnalyser = undefined;
    this.remoteMixConnected = false;
    return result;
  }

  /** Mute/unmute the learner's mic without dropping the call. */
  setMuted(muted: boolean) {
    this.micStream?.getAudioTracks()?.forEach((track) => (track.enabled = !muted));
  }

  private reset() {
    this.clearTimers();
    this.transcript = [];
    this.micChunks = [];
    this.mixedChunks = [];
    // A new call builds a new mix, so the previous call's wiring must not be assumed.
    this.remoteMixConnected = false;
    this.endedEmitted = false;
    this.greetingSent = false;
    this.assistantTextBuffer = '';
    this.assistantShown = '';
    this.assistantDoneAt = 0;
    this.userTextBuffer = '';
    this.userTurnStartedAt = 0;
    this.userTurnEndedAt = 0;
    this.assistantTurnStartedAt = 0;
    this.startedAtMs = 0;
    this.active = false;
    this.serverSession = undefined;
    this.manualResponseMode = false;
    this.responseInFlight = false;
    this.pendingResponseRequest = false;
    this.idleNudges = 0;
    this.noiseDrops = 0;
    this.interruptedActiveResponse = false;
    this.lastCommittedItemId = '';
    this.userTurnEntry = undefined;
    this.userTurnReleased = false;
    this.revealStartedAt = 0;
    this.botAudibleAt = 0;
    this.toolCallNames.clear();
    this.botAudioSeen = false;
    this.botAudioPlaying = false;
    this.botAudioStartedAt = 0;
    this.assistantTurnClosed = false;
    this.turnByItem.clear();
    this.sawAudioBufferEvents = false;
    this.loggedUnheardBot = false;
    this.assistantAudioEndsAt = 0;
    this.micRecorder = undefined;
    this.mixedRecorder = undefined;
    this.remoteAnalyser = undefined;
    this.mixedDestination = undefined;
    this.audioContext = undefined;
  }

  private clearTimers() {
    clearTimeout(this.greetingFallbackTimer);
    clearTimeout(this.maxDurationTimer);
    clearTimeout(this.userTranscriptTimer);
    this.greetingFallbackTimer = undefined;
    this.maxDurationTimer = undefined;
    this.userTranscriptTimer = undefined;
    clearTimeout(this.disconnectTimer);
    this.disconnectTimer = undefined;
    clearTimeout(this.botFinishTimer);
    this.botFinishTimer = undefined;
    clearTimeout(this.idleTimer);
    this.idleTimer = undefined;
    this.pendingBotEnd = false;
    this.stopReveal();
  }

  // ── Data channel ────────────────────────────────────────────────────────────

  /** One place where every outbound client event goes, so a closed channel is never a throw. */
  private send(payload: any) {
    if (this.dataChannel?.readyState !== 'open') { return; }
    try {
      this.dataChannel.send(JSON.stringify(payload));
    } catch (e) {
      console.log('[practice] realtime send failed', payload?.type, e);
    }
  }

  /** The bot speaks first — but only once, however many session events arrive. */
  private triggerBotGreeting() {
    if (this.greetingSent || this.dataChannel?.readyState !== 'open') { return; }
    this.greetingSent = true;
    this.send({ type: 'response.create' });
  }

  /**
   * Tightens turn detection and hands response creation to us. True when an update was sent (the
   * caller then waits for session.updated before greeting).
   *
   * PATCHES the session the server minted rather than composing a fresh one: session.update replaces
   * the objects it is given, so sending only `turn_detection` would drop the transcription config
   * with it and no learner line would ever appear.
   */
  private applySessionTuning(): boolean {
    const session = this.serverSession;
    if (!session || this.dataChannel?.readyState !== 'open') { return false; }

    const audio = session.audio || {};
    const input = audio.input || {};
    // No transcription model means no learner line is coming, so there is nothing to hold for.
    const gateOnTranscript = this.waitForUserTranscript && !!input.transcription;
    // Decides the dead air between turns: gpt-4o-transcribe streams deltas WHILE they talk,
    // whisper-1 only reports once, after the fact.
    console.log('[practice] input transcription:', input.transcription?.model || 'none');

    const detectionType = input.turn_detection?.type || 'server_vad';
    const turnDetection: any = {
      ...(input.turn_detection || {}),
      type: detectionType,
      // We send response.create ourselves, once the learner's turn is in the transcript.
      create_response: !gateOnTranscript,
      // The learner must always be able to talk over the coach.
      interrupt_response: true,
    };
    // threshold/padding/silence are server_vad-only knobs; semantic_vad rejects them.
    if (detectionType === 'server_vad') {
      turnDetection.threshold = this.vadThreshold;
      turnDetection.prefix_padding_ms = VAD_PREFIX_PADDING_MS;
      turnDetection.silence_duration_ms = this.vadSilenceMs;
    }

    this.send({
      event_id: SESSION_TUNING_EVENT_ID,
      type: 'session.update',
      // Nulls are stripped: session.created reports optional fields it will NOT accept back — e.g.
      // `audio.input.transcription.prompt: null` — and echoing one gets the WHOLE update rejected
      // ("expected a string, but got null instead"), so none of the turn-detection tuning applies.
      session: this.withoutNulls({
        type: session.type || 'realtime',
        audio: {
          ...audio,
          input: {
            ...input,
            turn_detection: turnDetection,
            ...(this.noiseReduction ? { noise_reduction: { type: this.noiseReduction } } : {}),
          },
        },
      }),
    });
    // Optimistic: session.updated corrects it. If that ack is lost while the patch DID apply,
    // waiting for it would mean nobody ever answers the learner.
    this.manualResponseMode = gateOnTranscript;
    return true;
  }

  /**
   * Deep copy without null/undefined members. The realtime API emits nulls for unset optional fields
   * but rejects them as input, so anything echoed back from session.created has to be pruned first.
   */
  private withoutNulls(value: any): any {
    if (Array.isArray(value)) { return value.map((item) => this.withoutNulls(item)); }
    if (value && typeof value === 'object') {
      const pruned: any = {};
      Object.keys(value).forEach((key) => {
        if (value[key] === null || value[key] === undefined) { return; }
        pruned[key] = this.withoutNulls(value[key]);
      });
      return pruned;
    }
    return value;
  }

  /** session.updated is authoritative about who creates responses from here on. */
  private confirmSessionTuning(session: any) {
    const detection = session?.audio?.input?.turn_detection ?? session?.turn_detection;
    if (!detection) { return; }   // says nothing about auto-answering — leave the mode alone
    this.manualResponseMode = detection.create_response === false;
    // OFF means the server answers on its own, so the on-screen order is not ours to guarantee.
    console.log('[practice] transcript-first replies:', this.manualResponseMode ? 'on' : 'off');
  }

  private handleServerEvent(event: MessageEvent) {
    let payload: any;
    try {
      payload = JSON.parse(event?.data);
    } catch (e) {
      return;   // non-JSON frame: nothing to do
    }

    const type: string = payload?.type ?? '';

    // Event names differ between the beta and GA realtime APIs (`response.audio_transcript.delta`
    // vs `response.output_audio_transcript.delta`, and the text-only variants), so match on shape
    // rather than exact strings — otherwise a rename silently kills the on-screen transcript.
    if (type === 'session.created') {
      this.serverSession = payload?.session;
      // The call ends when the model CALLS end_conversation, and that tool is declared server-side
      // in the ephemeral session — not here. If it is absent the model can never ask to finish, the
      // call runs to its time limit, and nothing in the client explains why. Say so once.
      const tools = (payload?.session?.tools || []).map((tool: any) => tool?.name ?? tool?.function?.name);
      if (!tools.includes('end_conversation')) {
        console.log('[practice] session declares no end_conversation tool — the coach cannot end the '
          + 'call itself; tools seen:', tools);
      }
      // Greet only after the tuning lands, so our session.update never races the first response.
      if (!this.applySessionTuning()) { this.triggerBotGreeting(); }
      return;
    }
    if (type === 'session.updated') {
      this.confirmSessionTuning(payload?.session);
      this.triggerBotGreeting();
      return;
    }

    // ── Learner turn ──
    if (type.endsWith('input_audio_buffer.speech_started')) {
      this.userTurnStartedAt = Date.now();
      this.userTurnEndedAt = 0;
      this.userTextBuffer = '';
      this.userTurnEntry = undefined;
      this.userTurnReleased = false;
      // They are talking, so the silence run is over. The counter resets too: someone who pauses
      // twice in a long call must not be hung up on for it.
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
      this.idleNudges = 0;
      this.interruptedActiveResponse = this.responseInFlight;
      if (this.responseInFlight) {
        // Take the floor ourselves rather than waiting for the server to do it. We ask for
        // interrupt_response: true, but no output_audio_buffer.cleared has ever arrived in practice,
        // so the coach talks straight over the learner. `response.cancel` stops generating and
        // `output_audio_buffer.clear` drops the audio already sent to this client — without the
        // second one the rest of the reply still plays out of the speaker.
        this.send({ type: 'response.cancel' });
        this.send({ type: 'output_audio_buffer.clear' });
        // The text arrives far ahead of the voice, so the reveal is still paying out a sentence the
        // learner will now never hear. Left running it races on in silence, and the turn is recorded
        // as though the coach said all of it. Keep only what was actually spoken.
        this.truncateAssistantTurnToWhatWasHeard();
      }
      // Nothing transcribed yet, but the screen should show that the learner has the floor.
      this.liveTurn$.next({ role: 'user', text: '' });
      return;
    }
    // Keep this SEPARATE from speech_started: merged, ended_at_ms is stamped at the start of the
    // utterance (zero-length turns, wrong metrics) and the transcript wait runs while they talk.
    if (type.endsWith('input_audio_buffer.speech_stopped')) {
      this.userTurnEndedAt = Date.now();
      // Text already streamed = their line is on screen, so answer now rather than sitting through
      // the ASR round trip. `completed` still lands and supersedes the text in place.
      if (this.isMeaningfulUserSpeech(this.userTextBuffer)) {
        // No item id yet, deliberately: the server commits the buffer just AFTER this, so
        // lastCommittedItemId still names the PREVIOUS utterance. Binding that here would file this
        // turn under its predecessor's id. The committed handler binds it a moment later.
        this.releaseUserTurn(this.userTextBuffer);
      } else {
        this.startUserTranscriptWait();
      }
      return;
    }
    if (type.endsWith('input_audio_buffer.committed')) {
      this.lastCommittedItemId = payload?.item_id || '';
      // The commit routinely lands AFTER the turn was published: speech_stopped releases it from the
      // streamed text, and the server commits the buffer a beat later. Bind the id now, or the late
      // transcript for this item arrives with no turn to attach to.
      this.bindItemToTurn(this.lastCommittedItemId);
      return;
    }
    if (type.includes('audio_transcription.delta')) {
      // Still arriving for an utterance already recorded — see supersedeRecordedTurn. Buffering it
      // here splices what they said before the interruption into what they are saying now.
      if (this.isRecordedItem(payload?.item_id)) { return; }
      this.userTextBuffer += payload?.delta ?? payload?.transcript ?? '';
      // After release the line is committed: keep buffering (the `completed` fallback reads it) but
      // don't re-open a live bubble underneath it.
      if (!this.userTurnReleased) {
        this.liveTurn$.next({ role: 'user', text: this.userTextBuffer });
      }
      return;
    }
    if (type.includes('audio_transcription.completed') || type.includes('audio_transcription.done')) {
      if (this.supersedeRecordedTurn(payload?.item_id, payload?.transcript)) { return; }
      this.releaseUserTurn(payload?.transcript || this.userTextBuffer, payload?.item_id);
      return;
    }
    if (type.includes('audio_transcription.failed')) {
      // Nothing to show, but the audio IS in the model's context — let the coach answer it.
      this.releaseUserTurn('', payload?.item_id, true);
      return;
    }

    // ── Bot turn ── (audio transcript OR text-only deltas, beta and GA spellings)
    if (type === 'response.created') {
      // Whatever is still buffered belongs to the previous reply. Wiping it here loses that turn
      // outright — record it before the buffer is reused.
      if (this.assistantTextBuffer) { this.commitAssistantTurn(); }
      this.responseInFlight = true;
      this.assistantTurnStartedAt = Date.now();
      this.assistantTextBuffer = '';
      // Per TURN, not per call: these were only ever reset in reset(), so a new reply inherited the
      // previous one's "last audible at" — a stamp from before the learner spoke. The caption then
      // read that as a stream it cannot hear and paid itself out on the clock, in silence, in the
      // gap before playback started.
      this.botAudioStartedAt = 0;
      this.botAudioPlaying = false;
      this.botAudibleAt = 0;
      this.assistantTurnClosed = false;
      return;
    }
    if (this.isAssistantTextDelta(type)) {
      // Straggler from a reply already recorded — see assistantTurnClosed.
      if (this.assistantTurnClosed) { return; }
      this.assistantTextBuffer += payload?.delta ?? '';
      if (!this.assistantTurnStartedAt) { this.assistantTurnStartedAt = Date.now(); }
      this.pumpReveal();
      return;
    }
    if (this.isAssistantTextDone(type)) {
      if (this.assistantTurnClosed) { return; }
      this.markAssistantDone(payload?.transcript || payload?.text);
      return;
    }
    // ── The coach's audio, as the transport actually reports it ──
    // WebRTC sessions bracket playback with output_audio_buffer.started/stopped, and signal the end
    // of generation with response.output_audio.done. These are authoritative: before handling them
    // the code inferred the end of speech from a characters-per-second estimate, which is why a
    // reply's last words could be dropped while its transcript was complete.
    if (type.endsWith('output_audio_buffer.started')) {
      this.sawAudioBufferEvents = true;
      this.botAudioPlaying = true;
      this.botAudioSeen = true;
      this.botAudibleAt = Date.now();
      // The voice is out of the speaker: the caption may start moving.
      if (!this.botAudioStartedAt) { this.botAudioStartedAt = Date.now(); }
      return;
    }
    if (type.endsWith('output_audio_buffer.stopped') || type.endsWith('output_audio_buffer.cleared')) {
      this.sawAudioBufferEvents = true;
      this.botAudioPlaying = false;
      this.botAudibleAt = Date.now();
      // The buffer has drained, so the voice really has finished — no estimate needed.
      this.assistantAudioEndsAt = Date.now();
      // The learner's silence starts when the coach stops talking, so the countdown starts here.
      // `cleared` is the other case — a barge-in — where they already have the floor and are using
      // it; their own speech_started cleared this timer and their turn ending will set it again.
      if (type.endsWith('output_audio_buffer.stopped')) { this.armIdleTimer(); }
      return;
    }
    if (/^response\..*output_audio\.done$/.test(type)) {
      // Generation finished; the buffer may still be playing out. Left to the stopped event.
      return;
    }

    if (type === 'response.done') {
      this.responseInFlight = false;
      // On the GA API a tool call is only visible here, inside response.output[]. This handler
      // returns, so checking it anywhere below would never run.
      if (this.isEndConversation(payload)) { this.endAfterBotFinishesSpeaking(); }
      // Generation finishes long before the voice does: the model writes a forty-second reply in a
      // few seconds and then streams it. Measured from here, the coach's own speaking time came out
      // of the learner's allowance — a long answer was followed almost at once by "are you still
      // with me?". The audio-stopped event above re-arms this exactly; the estimate covers the turn
      // until it arrives, and a transport that never sends one.
      this.armIdleTimer(this.assistantAudioEndsAt - Date.now());
      // Safety net: if the per-turn "done" event never arrives under any known name, the response
      // itself completing still commits whatever was streamed, so a turn is never lost.
      if (this.assistantTextBuffer) { this.markAssistantDone(''); }
      // A reply we owed while this one was still streaming (the learner talked over the coach).
      if (this.pendingResponseRequest) {
        this.pendingResponseRequest = false;
        this.maybeCreateResponse();
      }
      return;
    }

    // ── Bot decided the conversation is over ──
    // A tool call is spread over several events, and the two facts needed to recognise it arrive on
    // different ones: output_item.added carries item.name, while function_call_arguments.done
    // carries only call_id and the arguments. Watching a single event type therefore misses the
    // call outright depending on the API version — so the name is remembered when it is announced,
    // and every event that could complete the call is checked.
    if (type === 'response.output_item.added' || type === 'conversation.item.added'
      || type === 'response.output_item.done' || type === 'conversation.item.done'
      || type === 'response.function_call_arguments.done') {
      this.rememberToolCall(payload?.item);
      if (this.isEndConversation(payload)) { this.endAfterBotFinishesSpeaking(); }
      return;
    }

    if (type === 'error') {
      // Our tuning was refused, so the server still answers by itself. Hand the wheel back, or every
      // turn gets answered twice.
      if (payload?.error?.event_id === SESSION_TUNING_EVENT_ID) {
        console.log('[practice] session tuning rejected — server keeps auto-answering');
        this.manualResponseMode = false;
        clearTimeout(this.userTranscriptTimer);
      }
      this.error$.next(payload);
      return;
    }

    this.logUnhandledEvent(type);
  }

  /** `response.audio_transcript.delta`, `response.output_audio_transcript.delta`, `response.output_text.delta`… */
  private isAssistantTextDelta(type: string): boolean {
    return /^response\..*(audio_transcript|text)\.delta$/.test(type);
  }

  private isAssistantTextDone(type: string): boolean {
    return /^response\..*(audio_transcript|text)\.done$/.test(type);
  }

  // ── Transcript-first replies, and noise that gets no reply at all ────────────
  // Transcribing the learner lags the model's own reply, so with the server auto-answering they HEAR
  // the answer before they SEE what they said. We turn that off (create_response: false) and create
  // the response here, once their line is committed — and drop VAD triggers that transcribed to
  // nothing instead of answering them.

  /** After speech stops, the transcript has this long to land before the reply goes ahead anyway. */
  private startUserTranscriptWait() {
    if (!this.manualResponseMode) { return; }
    clearTimeout(this.userTranscriptTimer);
    this.userTranscriptTimer = setTimeout(
      () => this.releaseUserTurn(this.userTextBuffer, this.lastCommittedItemId, true),
      USER_TRANSCRIPT_WAIT_MS
    );
  }

  /**
   * The learner's turn is settled: show it (or drop it as noise), then let the coach answer.
   * `timedOut` means we never got a transcript — the learner did speak, so answer regardless.
   */
  /**
   * The turn each input item was transcribed into, so a transcript that arrives late can find it.
   *
   * The ASR runs behind the VAD by design, so after a pause or a barge-in the transcript for what
   * the learner said a moment ago lands while they are already speaking again.
   */
  private turnByItem = new Map<string, PracticeTranscriptTurn>();

  /** Notes which item produced the turn just published. */
  private bindItemToTurn(itemId?: string): void {
    if (itemId && this.userTurnReleased && this.userTurnEntry) {
      this.turnByItem.set(itemId, this.userTurnEntry);
    }
  }

  private isRecordedItem(itemId?: string): boolean {
    return !!itemId && this.turnByItem.has(itemId);
  }

  /**
   * A transcript for an utterance that is already on screen: correct that line and touch nothing
   * else.
   *
   * Applied to the live turn instead — which is what happened before this, because the handler read
   * the transcript and ignored the item it belonged to — one late transcript did four things at
   * once: published the earlier utterance a second time, stamped with the CURRENT turn's clock so it
   * sorted into the wrong place; took over as the entry to supersede, so the words being spoken then
   * overwrote it; consumed the interrupted-response flag; and marked the live turn as already
   * released, which is why the coach never answered what was actually being asked.
   */
  private supersedeRecordedTurn(itemId: string | undefined, transcript: string): boolean {
    const entry = itemId ? this.turnByItem.get(itemId) : undefined;
    if (!entry) { return false; }
    const spoken = (transcript || '').trim();
    if (spoken && spoken !== entry.text) {
      entry.text = spoken;
      this.transcriptChange$.next(this.transcript);
    }
    return true;
  }

  private releaseUserTurn(text: string, itemId?: string, timedOut = false) {
    clearTimeout(this.userTranscriptTimer);
    this.userTranscriptTimer = undefined;

    const spoken = (text || '').trim();
    const isSpeech = this.isMeaningfulUserSpeech(spoken);
    // How long the VAD actually heard them for. An utterance too long to be noise earns a reply even
    // with no transcript: dropping it silently is what left the coach hanging until the learner
    // repeated themselves.
    const heardFor = this.userTurnStartedAt
      ? (this.userTurnEndedAt || Date.now()) - this.userTurnStartedAt
      : 0;
    const tooLongForNoise = heardFor >= MAX_NOISE_MS;

    if (isSpeech && this.userTurnEntry) {
      // A late transcript, or the same turn under both spellings: supersede the published line
      // rather than adding a second one.
      this.userTurnEntry.text = spoken;
      this.transcriptChange$.next(this.transcript);
    } else if (isSpeech) {
      this.noiseDrops = 0;   // they were heard, so the run of noise-only turns is over
      this.userTurnEntry = this.pushTurn('user', spoken, this.userTurnStartedAt, this.userTurnEndedAt);
    } else if (!timedOut && !this.userTurnEntry && !tooLongForNoise) {
      // Transcribed to nothing = nothing said. Never on the timeout path (that audio is real), never
      // once a line is on screen (a `completed` can arrive carrying no transcript), and never for an
      // utterance too long to be noise — that audio stays in context for the coach to answer.
      this.discardNoiseItem(itemId || this.lastCommittedItemId);
      if (++this.noiseDrops === NOISE_WARNING_AFTER) { this.noiseWarning$.next(); }
    } else if (!isSpeech && tooLongForNoise) {
      console.log('[practice] learner spoke for', heardFor, 'ms with no transcript — replying anyway');
    }
    this.liveTurn$.next(null);   // clears "You: speaking…", committed or dropped

    // userTextBuffer and the speech stamps survive until the next speech_started: they are the
    // fallback text, and the "when it was spoken" position, for a late transcript.
    this.lastCommittedItemId = '';

    // Noise earns no reply — unless it ran long, or it cut the coach off: silence is worse.
    const owesReply = !this.userTurnReleased
      && (isSpeech || timedOut || tooLongForNoise || this.interruptedActiveResponse);
    this.userTurnReleased = true;
    this.interruptedActiveResponse = false;
    // Whichever branch published it, the line now belongs to an item — see supersedeRecordedTurn.
    // Only an id the caller was given: see the speech_stopped path for why the last committed one
    // cannot be assumed to be this turn's.
    this.bindItemToTurn(itemId);
    if (owesReply) { this.maybeCreateResponse(); }
  }

  /**
   * A cough or a door transcribes to '' or bare punctuation. Two real characters is the floor, so
   * short answers ("Hi", "No", "OK") survive.
   */
  private isMeaningfulUserSpeech(text: string): boolean {
    return (text || '').replace(PUNCTUATION, '').length >= MIN_USER_SPEECH_CHARS;
  }

  /** The VAD committed that noise as an item; drop it or the model carries it into later replies. */
  private discardNoiseItem(itemId: string) {
    if (!itemId) { return; }
    this.send({ type: 'conversation.item.delete', item_id: itemId });
  }

  // ── Silence ──────────────────────────────────────────────────────────────────
  // Armed when the coach stops talking, cancelled when the learner starts. Nothing else in the
  // pipeline can notice a learner who simply says nothing.

  /**
   * Starts the wait before asking whether the learner is still there.
   *
   * `speakingMs` is what the coach still has left to say: the silence being measured is the
   * learner's, and it cannot begin until the voice they are listening to has finished.
   */
  private armIdleTimer(speakingMs = 0) {
    clearTimeout(this.idleTimer);
    this.idleTimer = undefined;
    if (!this.active || this.endedEmitted) { return; }
    if (this.dataChannel?.readyState !== 'open') { return; }
    const wait = IDLE_NUDGE_MS + Math.max(0, speakingMs || 0);
    this.idleTimer = setTimeout(() => this.nudgeIdleLearner(), wait);
  }

  private nudgeIdleLearner() {
    this.idleTimer = undefined;
    if (!this.active || this.endedEmitted) { return; }
    // The coach still holds the floor, so this silence is its turn, not the learner's.
    if (this.responseInFlight) { this.armIdleTimer(); return; }

    if (this.idleNudges >= MAX_IDLE_NUDGES) {
      // Nobody there. 'timeout', not 'user_ended' — nobody chose to hang up.
      this.endCall('timeout');
      return;
    }
    // Injected as a system message, NOT as `response.instructions`. That field REPLACES the session
    // instructions for the response it is attached to, so the persona, the behaviour rules and — most
    // visibly — the language the session was minted in all vanish for exactly that turn, and the
    // check-in can come back in another language. As an item the direction is added to the context
    // and everything the session already established still applies.
    this.send({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'system',
        content: [{ type: 'input_text', text: IDLE_INSTRUCTIONS[this.idleNudges] }],
      },
    });
    this.send({ type: 'response.create' });
    this.idleNudges++;
    // Re-armed here rather than left to response.done: a model that never answers the nudge would
    // otherwise strand the timer and hold the session open, and billed, indefinitely.
    this.armIdleTimer();
  }

  /** Ask for a reply — now if the channel is idle, otherwise as soon as the current one ends. */
  private maybeCreateResponse() {
    if (!this.manualResponseMode) { return; }   // the server is still answering by itself
    if (this.responseInFlight) {
      this.pendingResponseRequest = true;
      return;
    }
    this.send({ type: 'response.create' });
  }

  // ── Paced reveal ────────────────────────────────────────────────────────────
  // The model emits the whole transcript far faster than it speaks it, so revealing on arrival dumps
  // the entire reply on screen while the audio is still on its first sentence. The reveal is paced by
  // the audio instead: a character per tick while the bot is audible, held while it is not.

  /** The done event has landed — the full text is known, only the reveal is outstanding. */
  private assistantDoneAt = 0;
  /** Characters revealed so far. */
  private assistantShown = '';
  private revealTimer: any;
  /** When the current reveal began — the reference for "the analyser has said nothing yet". */
  private revealStartedAt = 0;
  /** Last time the remote track was audible, and whether it EVER was (false = analyser is deaf). */
  private botAudibleAt = 0;
  private botAudioSeen = false;
  /** True between output_audio_buffer.started and .stopped — the coach is actually being heard. */
  private botAudioPlaying = false;
  /** The transport reports playback boundaries, so estimates are no longer needed for this call. */
  private sawAudioBufferEvents = false;
  /** A microphone already being acquired before the session existed (see prewarmMicrophone). */
  private micPrewarm?: Promise<MediaStream>;
  /** Keeps the "can't hear the bot" note to once per call, not once per reveal tick. */
  private loggedUnheardBot = false;
  /** Clock estimate of when the bot's current line should stop being spoken. */
  private assistantAudioEndsAt = 0;
  /**
   * When playback of THIS turn began, per output_audio_buffer.started — 0 until the voice starts.
   *
   * The caption is held until it is set. The model streams a whole reply in a few hundred ms and
   * playback starts later still, so a caption that begins on the first text delta is already
   * seconds ahead of the speaker: the words scroll past in silence.
   */
  private botAudioStartedAt = 0;
  /**
   * This reply has been recorded and is finished with.
   *
   * A cancelled response goes on sending its transcript: after a barge-in the `.done` for the reply
   * just truncated still arrives. Left to run it refills the buffer — with the FULL text, most of
   * which was never spoken — while assistantTurnStartedAt has already been reset, so the turn is
   * pushed with a start stamp of "now" and sorts to the bottom of the transcript, answering a
   * question from minutes earlier.
   */
  private assistantTurnClosed = false;

  private markAssistantDone(finalText: string) {
    if (finalText) { this.assistantTextBuffer = finalText; }
    // Stamp the turn's END now, not when the reveal finishes — the server derives response latency
    // from these stamps and must see when speech actually stopped.
    this.assistantDoneAt = Date.now();
    // The text completes long before the voice does. Estimate when the audio runs out so a closing
    // line can still be waited for on a stream we cannot hear (endAfterBotFinishesSpeaking).
    const startedAt = this.assistantTurnStartedAt || Date.now();
    this.assistantAudioEndsAt =
      startedAt + AUDIO_LEAD_IN_MS + this.assistantTextBuffer.length * SPOKEN_MS_PER_CHAR;
    this.pumpReveal();
  }

  private pumpReveal() {
    if (!this.revealMsPerChar) {          // pacing off (tests) — reveal everything immediately
      this.assistantShown = this.assistantTextBuffer;
      this.liveTurn$.next({ role: 'assistant', text: this.assistantShown });
      if (this.assistantDoneAt) { this.commitAssistantTurn(); }
      return;
    }
    if (this.revealTimer) { return; }     // already draining
    this.revealStartedAt = Date.now();
    this.revealTimer = setInterval(() => {
      const remaining = this.assistantTextBuffer.length - this.assistantShown.length;
      // The transport brackets playback with output_audio_buffer.started/.stopped. Where it does,
      // those are exact and the analyser is not consulted: RMS reads the gaps between words as
      // silence, which is a guess about the voice where the transport is telling us outright.
      const bracketed = this.sawAudioBufferEvents;
      const speaking = bracketed ? this.botAudioPlaying : this.isBotSpeaking();
      const quietForMs = Date.now() - (this.botAudibleAt || this.revealStartedAt);
      // Silence we can't interpret — no analyser, or it has said nothing for long enough that it
      // clearly can't hear this stream. The caption then falls back to the clock.
      const unheard = bracketed
        // Playback of this turn has not begun. Waiting is right, up to the point where no audio is
        // plainly coming at all (a text-only reply) and a blank caption would be the worse answer.
        ? !this.botAudioStartedAt && Date.now() - this.revealStartedAt > REVEAL_AUDIO_WAIT_MS
        : !this.remoteAnalyser || quietForMs > REVEAL_AUDIO_WAIT_MS;
      if (unheard && !this.botAudioSeen && !this.loggedUnheardBot) {
        this.loggedUnheardBot = true;
        console.log('[practice] cannot hear the bot track — captions revert to the clock');
      }
      // Nothing left to pace against: the text is complete and the voice has stopped (or was never
      // audible), so drain the rest quickly. The analyser window is ~10ms and gaps between words read
      // as silence, so "stopped" must mean quiet for a beat or the first pause flushes the caption.
      const voiceStopped = bracketed
        ? !!this.botAudioStartedAt && !this.botAudioPlaying
        : this.botAudioSeen && quietForMs > AUDIO_STOPPED_MS;
      const draining = !!this.assistantDoneAt && (unheard || voiceStopped);

      if (remaining > 0 && (speaking || draining || unheard)) {
        const chunk = draining
          ? Math.max(1, Math.ceil(remaining / Math.max(1, CATCH_UP_MS / this.revealMsPerChar)))
          : 1;
        this.assistantShown = this.assistantTextBuffer.slice(0, this.assistantShown.length + chunk);
        this.liveTurn$.next({ role: 'assistant', text: this.assistantShown });
      }
      if (this.assistantShown.length >= this.assistantTextBuffer.length && this.assistantDoneAt) {
        this.commitAssistantTurn();
      }
    }, this.revealMsPerChar);
  }

  /**
   * The learner cut in: stop the caption and keep the words that were actually voiced.
   *
   * The transcript streams whole sentences ahead of the audio, so at the moment of an interruption
   * `assistantTextBuffer` holds much more than was ever spoken. What was heard is the honest record
   * of the turn, and it is also what the analysis should score, since the learner only responded to
   * what reached them.
   *
   * Measured two ways, whichever is further on. The paced reveal is one, but it is not enough by
   * itself: it only advances once playback is reported, it advances a character at a time, and where
   * that report is late it holds for up to REVEAL_AUDIO_WAIT_MS. A learner cutting in early left it
   * with nothing released — and then the coach's half-spoken answer was dropped from the transcript
   * altogether, which reads as the transcript stopping mid-reply. The other is the clock: how much
   * of the line the voice has had time to say since it became audible.
   */
  private truncateAssistantTurnToWhatWasHeard() {
    this.stopReveal();
    if (!this.assistantTextBuffer) { return; }

    // The reported start of playback. Estimated only where the transport does not report one at
    // all: where it does, botAudioStartedAt of 0 is not a gap in what we know, it is the transport
    // saying this reply never played — the same evidence pumpReveal holds the caption on. Letting
    // the estimate answer over it recorded a silent reply as partly spoken, and the analysis then
    // scored the learner against words the coach never said.
    const audibleFrom = this.botAudioStartedAt
      || (!this.sawAudioBufferEvents && this.assistantTurnStartedAt
        ? this.assistantTurnStartedAt + AUDIO_LEAD_IN_MS
        : 0);
    const spokenChars = audibleFrom
      ? Math.floor(Math.max(0, Date.now() - audibleFrom) / SPOKEN_MS_PER_CHAR)
      : 0;
    const heard = this.wholeWordsWithin(
      this.assistantTextBuffer,
      Math.max((this.assistantShown || '').length, spokenChars),
    );
    if (!heard) {
      // The voice never started: nothing was heard, so there is no turn to record.
      this.assistantTextBuffer = '';
      this.assistantShown = '';
      this.assistantTurnStartedAt = 0;
      this.assistantDoneAt = 0;
      this.assistantTurnClosed = true;
      this.liveTurn$.next(null);
      return;
    }
    this.assistantTextBuffer = heard;
    this.commitAssistantTurn();
  }

  /**
   * The first `count` characters of a line, ending on a word rather than inside one.
   *
   * A cut-off reply is already a fragment; leaving it mid-word ("about the deadl") reads as a
   * rendering fault rather than as an interruption.
   */
  private wholeWordsWithin(text: string, count: number): string {
    if (count <= 0) { return ''; }
    if (count >= text.length) { return text.trim(); }
    const slice = text.slice(0, count);
    // Already at a boundary: the next character starts a new word.
    if (/\s/.test(text[count])) { return slice.trim(); }
    const lastSpace = slice.lastIndexOf(' ');
    return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim();
  }

  private commitAssistantTurn() {
    this.stopReveal();
    this.pushTurn(
      'assistant',
      this.assistantTextBuffer,
      this.assistantTurnStartedAt,
      this.assistantDoneAt || Date.now()
    );
    this.assistantTextBuffer = '';
    this.assistantShown = '';
    this.assistantTurnStartedAt = 0;
    this.assistantDoneAt = 0;
    // Recorded: anything further about this reply is a straggler.
    this.assistantTurnClosed = true;
    this.liveTurn$.next(null);
  }

  private stopReveal() {
    if (this.revealTimer) {
      clearInterval(this.revealTimer);
      this.revealTimer = undefined;
    }
  }

  // ── Letting the bot finish its last sentence ────────────────────────────────
  // end_conversation arrives when the model DECIDES to end, while its closing audio is still playing.
  // Hold the call open until that audio goes quiet and the caption has caught up.

  private pendingBotEnd = false;
  /** call_id → tool name, so an arguments-done event that names no tool can still be resolved. */
  private toolCallNames = new Map<string, string>();
  private botFinishTimer: any;
  private remoteAnalyser: AnalyserNode;

  private endAfterBotFinishesSpeaking() {
    if (this.pendingBotEnd) { return; }
    if (!this.botFinishGraceMs) {          // waiting disabled (tests) — end straight away
      this.endCall('bot_ended');
      return;
    }
    this.pendingBotEnd = true;

    const startedWaitingAt = Date.now();
    // The cap must clear the closing line itself, or a long goodbye is cut off by its own guard.
    const deadline = Math.max(
      startedWaitingAt + BOT_FINISH_MAX_WAIT_MS,
      this.assistantAudioEndsAt + BOT_FINISH_SLACK_MS
    );
    const tick = () => {
      if (!this.active) { return; }        // already torn down elsewhere
      this.isBotSpeaking();                // refreshes botAudibleAt / botAudioSeen
      const quietFor = Date.now() - (this.botAudibleAt || startedWaitingAt);
      const revealPending = !!this.assistantTextBuffer;
      // An analyser that never heard the bot proves nothing by being silent, so fall back to how long
      // the streamed line should take to say. Without this the last turn is cut off on such streams.
      const audioFinished = this.sawAudioBufferEvents
        // The transport told us when playback stopped — trust that over any estimate.
        ? !this.botAudioPlaying
        : this.botAudioSeen
          ? quietFor >= this.botFinishGraceMs
          : Date.now() >= this.assistantAudioEndsAt;
      if ((audioFinished && !revealPending) || Date.now() >= deadline) {
        this.pendingBotEnd = false;
        this.endCall('bot_ended');
        return;
      }
      this.botFinishTimer = setTimeout(tick, 100);
    };
    tick();
  }

  /**
   * True while the remote (bot) track is producing sound, and the only place that records when it was
   * last audible — both the caption pace and the end-of-call wait read those two fields.
   */
  private isBotSpeaking(): boolean {
    if (!this.remoteAnalyser) { return false; }   // can't tell — callers fall back to the clock
    const samples = new Uint8Array(this.remoteAnalyser.fftSize);
    this.remoteAnalyser.getByteTimeDomainData(samples);
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const deviation = (samples[i] - 128) / 128;   // 128 is silence in time-domain data
      sumSquares += deviation * deviation;
    }
    const speaking = Math.sqrt(sumSquares / samples.length) > SPEAKING_RMS_THRESHOLD;
    if (speaking) {
      this.botAudibleAt = Date.now();
      this.botAudioSeen = true;
    }
    return speaking;
  }

  /** Logs each unrecognised event type ONCE — enough to spot a renamed event without flooding. */
  private logUnhandledEvent(type: string) {
    if (!type || this.seenUnhandledTypes.has(type)) { return; }
    this.seenUnhandledTypes.add(type);
    console.log('[practice] unhandled realtime event:', type);
  }

  /**
   * Whether this event is the model asking to end the call.
   *
   * Matched on shape rather than one field, because a tool call surfaces differently depending on
   * the event and the API version: named directly on function_call_arguments.done, on `item` for
   * the item events, by call_id alone on the GA arguments-done, and only inside `response.output[]`
   * on response.done. Missing any of those means the call never ends by itself and runs to its time
   * limit instead.
   */
  /**
   * Records which tool a call_id belongs to, from whichever event announces it.
   *
   * Dropped with the call, so a long conversation cannot accumulate them.
   */
  private rememberToolCall(item: any): void {
    const callId = item?.call_id;
    const name = item?.name ?? item?.function?.name;
    if (callId && name) { this.toolCallNames.set(callId, name); }
  }

  private isEndConversation(payload: any): boolean {
    if (payload?.name === 'end_conversation' || payload?.item?.name === 'end_conversation'
      || payload?.item?.function?.name === 'end_conversation') {
      return true;
    }
    // Named on an earlier event of the same call: function_call_arguments.done identifies itself by
    // call_id alone, so without the map the call it completes cannot be recognised.
    const callId = payload?.call_id || payload?.item?.call_id;
    if (callId && this.toolCallNames.get(callId) === 'end_conversation') { return true; }

    const outputs = payload?.response?.output || payload?.output || [];
    return Array.isArray(outputs) && outputs.some((item: any) =>
      item?.name === 'end_conversation' || item?.function?.name === 'end_conversation');
  }

  /** Returns the published turn so a later, better transcript can amend it in place. */
  private pushTurn(
    role: 'user' | 'assistant',
    text: string,
    startedAt: number,
    endedAt: number
  ): PracticeTranscriptTurn {
    const trimmed = (text || '').trim();
    if (!trimmed) { return undefined; }
    const ended = endedAt || Date.now();
    const turn: PracticeTranscriptTurn = {
      role,
      text: trimmed,
      // Fall back to the end stamp if we never saw the matching start event, so the pair is
      // always present — the server needs both to compute response latency.
      started_at_ms: startedAt || ended,
      ended_at_ms: ended,
    };

    // Insert by WHEN IT WAS SPOKEN, not when it arrived: a late learner transcript would otherwise
    // land below the reply it prompted — wrong on screen and wrong for the latency metric.
    let index = this.transcript.length;
    while (index > 0 && this.transcript[index - 1].started_at_ms > turn.started_at_ms) {
      index--;
    }
    this.transcript.splice(index, 0, turn);
    this.transcriptChange$.next(this.transcript);
    return turn;
  }

  // ── Media ───────────────────────────────────────────────────────────────────

  private handleRemoteTrack(event: RTCTrackEvent) {
    const incoming = event?.streams?.[0];
    if (!incoming) { return; }
    incoming.getAudioTracks().forEach((track) => this.remoteStream.addTrack(track));
    this.remoteStream$.next(this.remoteStream);
    this.connectRemoteToMix();   // so the bot is audible in the playback recording
  }

  /**
   * Each recorder is guarded on its own: the call is already up by now, so a recorder that throws must
   * cost the analysis or the replay clip, never the session.
   */
  private startRecording() {
    const mimeType = this.pickAudioMimeType();
    const options: any = mimeType ? { mimeType } : undefined;

    try {
      this.micRecorder = this.createRecorder(this.micStream, options);
      this.micRecorder.ondataavailable = (e: BlobEvent) => {
        if (e?.data?.size) { this.micChunks.push(e.data); }
      };
      this.micRecorder.start();
      // performance.now(), not Date.now(): a device clock that resyncs mid-call (NTP, a manual
      // change, a timezone hop on a plane) moves wall time and would report a length minutes out
      // — or negative, which clamps to 0 and is then STORED, first-write-wins, as a recording of
      // no length at all.
      this.micRecordingStartedAt = this.now();
    } catch (e) {
      console.log('[practice] mic recording unavailable — the report will have no audio analysis', e);
    }

    if (!this.recordPlaybackMix) {
      console.log('[practice] playback mix off — recording the learner only');
      return;
    }
    try {
      // Mixed track: mic + bot through one destination node.
      this.audioContext = this.createAudioContext();
      // Created after getUserMedia and the SDP exchange, so the opening tap is stale and the context
      // can come up suspended — and a suspended graph records silence.
      this.audioContext.resume?.()?.catch?.(() => { /* stays suspended; the mix will be quiet */ });
      this.mixedDestination = this.audioContext.createMediaStreamDestination();
      this.audioContext.createMediaStreamSource(this.micStream).connect(this.mixedDestination);

      // ontrack fires during setRemoteDescription — BEFORE this mix exists — so the earlier attempt
      // to connect the bot bailed out on a missing AudioContext and the replay clip captured the
      // learner only. Whichever side is ready last does the wiring; connectRemoteToMix is idempotent.
      this.connectRemoteToMix();

      this.mixedRecorder = this.createRecorder(this.mixedDestination.stream, options);
      this.mixedRecorder.ondataavailable = (e: BlobEvent) => {
        if (e?.data?.size) { this.mixedChunks.push(e.data); }
      };
      this.mixedRecorder.start();
    } catch (e) {
      // Drop the whole mix rather than leave half an audio graph wired into a live call.
      console.log('[practice] playback mix unavailable — recording the learner only', e);
      this.teardownMix();
    }
  }

  /**
   * Routes the bot's audio into the playback mix (and the analyser). Safe to call repeatedly and
   * from either side of the race — it connects the aggregated remoteStream once, so a second remote
   * track cannot double the bot's level in the clip.
   */
  private connectRemoteToMix() {
    if (this.remoteMixConnected) { return; }
    if (!this.audioContext || !this.mixedDestination) { return; }   // mix not built yet
    if (!this.remoteStream?.getAudioTracks?.().length) { return; }  // bot track not here yet
    try {
      const source = this.audioContext.createMediaStreamSource(this.remoteStream);
      source.connect(this.mixedDestination);
      // Tap the same source to tell when the bot is actually still speaking (see isBotSpeaking).
      if (this.audioContext.createAnalyser) {
        this.remoteAnalyser = this.audioContext.createAnalyser();
        this.remoteAnalyser.fftSize = 512;
        source.connect(this.remoteAnalyser);
      }
      this.remoteMixConnected = true;
    } catch (e) {
      // A stream with no audio track can't be mixed; playback just misses the bot side.
      console.log('Practice: could not add remote stream to the playback mix', e);
    }
  }

  /** Unwires a half-built mix so nothing is left hanging off the live call. */
  private teardownMix() {
    this.mixedRecorder = undefined;
    this.mixedDestination = undefined;
    this.remoteAnalyser = undefined;
    this.remoteMixConnected = false;
    try { this.audioContext?.close(); } catch (e) { /* never opened */ }
    this.audioContext = undefined;
  }

  /** webm everywhere except iOS, which only records mp4 in this WebView. */
  /**
   * MP4 first, deliberately.
   *
   * The replay has to play back on a device that is not necessarily the one that recorded it, and
   * these two containers are not interchangeable: iOS has no WebM decoder at all, so a call recorded
   * in a browser could never be listened to on the phone. MP4/AAC is the one both sides read —
   * Safari records it natively, and Chrome has supported recording it since v130.
   *
   * WebM stays as the fallback for browsers that cannot write MP4 (Firefox today). Those recordings
   * still play everywhere except iOS, which is where they were before.
   */
  private pickAudioMimeType(): string {
    const recorder: any = (window as any).MediaRecorder;
    if (!recorder?.isTypeSupported) { return ''; }
    if (recorder.isTypeSupported('audio/mp4')) { return 'audio/mp4'; }
    if (recorder.isTypeSupported('audio/webm')) { return 'audio/webm'; }
    return '';
  }

  private stopRecorder(recorder: MediaRecorder, chunks: Blob[]): Promise<Blob | undefined> {
    return new Promise((resolve) => {
      const toBlob = () =>
        chunks.length ? new Blob(chunks, { type: recorder?.mimeType || chunks[0]?.type }) : undefined;

      if (!recorder || recorder.state === 'inactive') {
        resolve(toBlob());
        return;
      }
      let settled = false;
      const settle = () => {
        if (settled) { return; }
        settled = true;
        clearTimeout(guard);
        resolve(toBlob());
      };
      // onstop is not guaranteed on every WebView; never hold the end/analysis path open on it.
      const guard = setTimeout(settle, RECORDER_STOP_MAX_WAIT_MS);
      recorder.onstop = settle;
      try {
        recorder.stop();
      } catch (e) {
        settle();
      }
    });
  }

  // ── Seams (overridden in tests; real implementations here) ──────────────────

  /**
   * Spelled out rather than `audio: true`: echo cancellation stops the bot's own voice returning
   * through the speakers and being answered as the learner, and suppression/AGC hold noise below VAD.
   */
  protected getMicrophoneStream(): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  }

  protected createPeerConnection(): RTCPeerConnection {
    return new RTCPeerConnection();
  }

  protected createMediaStream(): MediaStream {
    return new MediaStream();
  }

  protected createRecorder(stream: MediaStream, options?: any): MediaRecorder {
    return new MediaRecorder(stream, options);
  }

  protected createAudioContext(): AudioContext {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    return new Ctor();
  }
}
